import type { UserStatus } from '@ccex/shared';

const ALLOWED: Record<UserStatus, string[]> = {
  started: ['/language', '/email'],
  email_pending: ['/language', '/email', '/code'],
  active: ['/', '/lock', '/lock/reset'],
  blocked: ['/blocked'],
  // Never shown: the API answers 401 for a merged row and the re-login lands on the wallet row
  // (spec 2026-10-27 §5.3). Listed so the table stays exhaustive.
  merged: ['/blocked'],
};

/** Sub-trees an active user may enter; matched by prefix because these routes carry parameters. */
const ACTIVE_PREFIXES = ['/withdraw', '/operations', '/settings', '/exchange'];

export function routeForStatus(status: UserStatus): string {
  switch (status) {
    case 'started':
      return '/language';
    case 'email_pending':
      return '/code';
    case 'active':
      return '/';
    case 'blocked':
      return '/blocked';
    case 'merged':
      return '/blocked';
  }
}

export function isAllowed(status: UserStatus, path: string): boolean {
  if (ALLOWED[status].includes(path)) return true;
  return (
    status === 'active' &&
    ACTIVE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  );
}

/** A same-origin path (with query and hash) or null: never lets a query string steer us elsewhere. */
export function safeInternalPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;
  } catch {
    return null;
  }
}

const LOCK_PATHS = ['/lock', '/lock/reset'];

/**
 * Spec 2026-10-30 §6.2. Runs after the profile check and before the status check: a locked
 * session sees nothing but the lock screens; an unlocked one is bounced off them. `next` must be
 * a same-origin path: a Mini App is never steered to a foreign URL by a query string.
 */
export function lockRedirect(
  pinLocked: boolean,
  path: string,
  fullPath: string,
  next?: string,
): { path: string; query: { next: string } } | string | null {
  const onLockScreen = LOCK_PATHS.includes(path);
  if (pinLocked) {
    if (onLockScreen || path === '/loading') return null;
    return { path: '/lock', query: { next: fullPath } };
  }
  if (!onLockScreen) return null;
  return safeInternalPath(next) ?? '/';
}
